"use client";
import React, { useMemo, useState } from "react";
import { BadgePercent, Edit3Icon, Plus, Trash2, X } from "lucide-react";
import { createPortal } from "react-dom";
import { useExpenses } from "../context/ExpenseContext";

/* ================= MAIN PAGE ================= */

export default function ExpensesPage() {
  const {
    expenses,
    loading,
    addExpense,
    updateExpense,
    toggleStatus,
    deleteExpense,
  } = useExpenses();

  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
const [status, setStatus] = useState("active");
  /* ---------- FILTER & SORT ---------- */
  const filtered = useMemo(() => {
    let data = expenses.filter((d) =>
      d.name.toLowerCase().includes(search.toLowerCase())
    );

    if (sortBy === "az") data.sort((a, b) => a.name.localeCompare(b.name));
    if (sortBy === "za") data.sort((a, b) => b.name.localeCompare(a.name));
    if (sortBy === "status") data.sort((a, b) =>
      a.status.localeCompare(b.status)
    );

    return data;
  }, [expenses, search, sortBy]);

  /* ---------- ACTIONS ---------- */

  const save = async () => {
    if (!name.trim()) return;

    const payload = {
      name,
      description,
    };

    if (editing) {
      await updateExpense(editing.id, payload);
    } else {
      await addExpense(payload);
    }

    setModalOpen(false);
    setEditing(null);
    setName("");
    setDescription("");
  };

  const toggleStatusHandler = (id, status) => {
    toggleStatus(id, status);
  };

  const removeHandler = (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this expense?"
    );

    if (confirmDelete) {
      deleteExpense(id);
    }
  };

  /* ================= UI ================= */

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-teal-700 rounded-xl flex items-center justify-center shadow-sm">
            <BadgePercent className="w-6 h-6 text-white" />
          </div>

          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-teal-800 leading-tight">
              Expenses
            </h1>
            <p className="mt-1 text-gray-600">
              Manage your expense categories
            </p>
          </div>
        </div>

        <button
          onClick={() => {
            setModalOpen(true);
            setEditing(null);
            setName("");
            setDescription("");
          }}
          className="w-full sm:w-auto flex items-center justify-center gap-2 bg-teal-600 text-white px-5 py-2.5 sm:px-6 sm:py-3 rounded-xl hover:bg-teal-700 transition-colors font-medium text-sm sm:text-base"
        >
          <Plus className="w-5 h-5" />
          Add Expense
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat label="Total" value={expenses.length} />
        <Stat
          label="Active"
          value={expenses.filter((d) => d.status === "active").length}
          color="text-green-600"
        />
        <Stat
          label="Inactive"
          value={expenses.filter((d) => d.status === "inactive").length}
          color="text-red-600"
        />
      </div>

      {/* Search & Sort */}
      <div className="flex flex-col md:flex-row gap-4 bg-orange-50 border border-orange-200 rounded-xl p-4">
        <input
          placeholder="Search expense..."
          className="px-4 py-2 border rounded-xl w-full md:w-80"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className="px-4 py-2 border rounded-xl w-full md:w-52"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          <option value="">Sort By</option>
          <option value="az">Name A → Z</option>
          <option value="za">Name Z → A</option>
        </select>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto bg-white rounded-xl shadow-sm border border-gray-200">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-6 py-3 text-sm font-semibold text-gray-700">
                ID
              </th>
              <th className="px-6 py-3 text-sm font-semibold text-gray-700">
                Expense Name
              </th>
              <th className="px-6 py-3 text-sm font-semibold text-gray-700">
                Description
              </th>
              <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700">
                Actions
              </th>
            </tr>
          </thead>

          <tbody>
            {filtered.map((item) => (
              <tr key={item.id} className="border-t hover:bg-gray-50">
                <td className="px-6 py-4">{item.id}</td>

                <td className="px-6 py-4 font-medium">{item.name}</td>

                <td className="px-6 py-4 text-gray-600">
                  {item.description}
                </td>

                <td className="px-6 py-4">
                  <div className="flex justify-end">
                    <Actions
                      item={item}
                      onEdit={() => {
                        setEditing(item);
                        setName(item.name);
                        setDescription(item.description || "");
                        setModalOpen(true);
                      }}
                      onToggle={toggleStatusHandler}
                      onDelete={removeHandler}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-4">
        {filtered.map((item) => (
          <div key={item.id} className="bg-white rounded-xl shadow p-4 space-y-2">
            <div className="flex justify-between">
              <div>
                <p className="text-xs text-gray-500">ID</p>
                <p>{item.id}</p>
              </div>

              <Actions
                item={item}
                onEdit={() => {
                  setEditing(item);
                  setName(item.name);
                  setDescription(item.description || "");
                  setModalOpen(true);
                }}
                onToggle={toggleStatusHandler}
                onDelete={removeHandler}
              />
            </div>

            <div>
              <p className="text-xs text-gray-500">Name</p>
              <p className="font-medium">{item.name}</p>
            </div>

            <div>
              <p className="text-xs text-gray-500">Description</p>
              <p>{item.description}</p>
            </div>

            <StatusBadge status={item.status} />
          </div>
        ))}
      </div>

      {/* Modal */}
    {modalOpen &&
  createPortal(
    <div className="fixed inset-0 z-50 flex justify-end">

      {/* BACKDROP */}
      <div
        className="absolute inset-0 bg-black/60"
        onClick={() => setModalOpen(false)}
      />

      {/* RIGHT PANEL */}
      <div
        className="relative w-full max-w-md h-full bg-[#f3e7db] rounded-l-3xl shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >

        {/* HEADER */}
        <div className="relative p-6 border-b border-orange-200 text-center">
          <button
            onClick={() => setModalOpen(false)}
            className="absolute top-4 left-4 w-9 h-9 bg-black text-white rounded-full flex items-center justify-center"
          >
            <X size={16} />
          </button>

          <h2 className="text-2xl font-bold text-teal-700">
            {editing ? "Edit Expense" : "Add New Expense"}
          </h2>
          <div className="w-12 h-[3px] bg-orange-400 mx-auto mt-2 rounded-full" />
        </div>

        {/* FORM */}
        <div className="p-6">
          <div className="bg-[#e6d5c3] rounded-2xl p-5 space-y-5">

            {/* NAME */}
            <div>
              <label className="text-sm font-medium flex items-center gap-2 mb-2">
                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                Expense Name *
              </label>

              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter expense name..."
                maxLength={50}
                className="w-full px-4 py-3 rounded-xl border border-orange-200 outline-none"
              />

              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Make it clear and short</span>
                <span>{name.length}/50</span>
              </div>
            </div>

            {/* DESCRIPTION */}
            <div>
              <label className="text-sm font-medium flex items-center gap-2 mb-2">
                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                Description
              </label>

              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description..."
                maxLength={200}
                className="w-full px-4 py-3 rounded-xl border border-orange-200 outline-none"
              />

              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Optional</span>
                <span>{description.length}/200</span>
              </div>
            </div>

            {/* STATUS */}
            <div>
              <label className="text-sm font-medium flex items-center gap-2 mb-2">
                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                Status
              </label>

              <div className="flex gap-4">
                <button
                  onClick={() => setStatus("active")}
                  className={`px-4 py-2 rounded-full text-sm ${
                    status === "active"
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100"
                  }`}
                >
                  Active
                </button>

                <button
                  onClick={() => setStatus("inactive")}
                  className={`px-4 py-2 rounded-full text-sm ${
                    status === "inactive"
                      ? "bg-red-100 text-red-700"
                      : "bg-gray-100"
                  }`}
                >
                  Inactive
                </button>
              </div>
            </div>

          </div>

          {/* ACTION BUTTONS */}
          <div className="flex gap-4 mt-6">
            <button
              onClick={() => setModalOpen(false)}
              className="flex-1 py-3 rounded-xl border bg-white text-gray-600"
            >
              Cancel
            </button>

            <button
              onClick={save}
              className="flex-1 py-3 rounded-xl bg-teal-600 text-white font-semibold"
            >
              Save
            </button>
          </div>
        </div>

      </div>
    </div>,
    document.body
  )}
    </div>
  );
}

/* ================= COMPONENTS ================= */

const Stat = ({ label, value, color = "text-gray-900" }) => (
  <div className="bg-white border border-orange-100 rounded-xl p-4">
    <p className="text-xs text-gray-500">{label}</p>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
  </div>
);

const StatusBadge = ({ status }) => (
  <span
    className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
      status === "active"
        ? "bg-green-100 text-green-700"
        : "bg-red-100 text-red-700"
    }`}
  >
    {status}
  </span>
);

const Actions = ({ item, onEdit, onToggle, onDelete }) => {
  return (
    <div className="flex items-center gap-3 justify-end">
      <button
        onClick={() => onToggle(item.id, item.status)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full ${
          item.status === "active" ? "bg-green-600" : "bg-gray-300"
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white ${
            item.status === "active" ? "translate-x-6" : "translate-x-1"
          }`}
        />
      </button>

      <button onClick={onEdit} className="text-teal-600">
        <Edit3Icon size={16} />
      </button>

      <button onClick={() => onDelete(item.id)} className="text-red-500">
        <Trash2 size={16} />
      </button>
    </div>
  );
};